import { atom } from 'nanostores';
import { getStorageValue } from '@/hooks/browser/use-local-storage';

export function isLocalDevHelperEnvironment(): boolean {
  return Boolean(import.meta.env.DEV) || import.meta.env.VITE_ENABLE_EXTERNAL_UI_OVERRIDES === 'true';
}

export function isProductionDevHelperEnvironment(): boolean {
  return __CIO_ENV__ === 'production' && !isLocalDevHelperEnvironment();
}

type ProductionDevHelperAccess = {
  isFeatureEnabled: boolean;
  canUseDeveloperTools?: boolean;
};

export function hasProductionDevHelperAccess({
  isFeatureEnabled,
  canUseDeveloperTools,
}: ProductionDevHelperAccess): boolean {
  return canUseDeveloperTools === true && isFeatureEnabled;
}

// Outside production, localOnly tabs require local tooling; account-edit tools
// also require account/edit. Production uses a separate restricted allowlist.
export const DEV_HELPER_TABS = [
  { value: 'app_import_overrides', text: 'App import overrides', localOnly: true, requiresAccountEdit: false },
  { value: 'feature_flags', text: 'Feature flags', localOnly: false, requiresAccountEdit: true },
  { value: 'account', text: 'Account', localOnly: false, requiresAccountEdit: true },
  { value: 'populate_workspace', text: 'Populate workspace', localOnly: true, requiresAccountEdit: false },
  { value: 'analytics', text: 'Analytics', localOnly: true, requiresAccountEdit: false },
  { value: 'tanstack_devtools', text: 'TanStack', localOnly: true, requiresAccountEdit: false },
  { value: 'ai_prompting', text: 'AI prompting', localOnly: false, requiresAccountEdit: false },
  { value: 'pluma_tokens', text: 'Pluma tokens', localOnly: false, requiresAccountEdit: false },
] as const;

export type DevHelperTab = (typeof DEV_HELPER_TABS)[number]['value'];

type DevHelperTabAccess = {
  isLocalEnv: boolean;
  canEditAccount: boolean;
  isProduction?: boolean;
};

/** The tabs this build and viewer get, in display order. */
export function getVisibleDevHelperTabs({
  isLocalEnv: isLocal,
  canEditAccount,
  isProduction = false,
}: DevHelperTabAccess) {
  if (isProduction)
    return DEV_HELPER_TABS.filter(({ value }) => value === 'populate_workspace' || value === 'pluma_tokens');
  return DEV_HELPER_TABS.filter((tab) => (isLocal || !tab.localOnly) && (canEditAccount || !tab.requiresAccountEdit));
}

const ACTIVE_TAB_STORAGE_KEY = 'dev-helper-active-tab';

function getInitialActiveTab(): DevHelperTab {
  const stored: unknown = getStorageValue(ACTIVE_TAB_STORAGE_KEY, null);
  const match = DEV_HELPER_TABS.find((tab) => tab.value === stored);
  if (match != null) return match.value;
  return isLocalDevHelperEnvironment() ? 'app_import_overrides' : 'ai_prompting';
}

export const $isDevHelperOpen = atom<boolean>(false);

// Shared (rather than component-local) so the command dialog can jump straight
// to a tab. Persisted under the same key the dev helper always used.
export const $devHelperActiveTab = atom<DevHelperTab>(getInitialActiveTab());

export function setDevHelperActiveTab(tab: DevHelperTab) {
  $devHelperActiveTab.set(tab);
  try {
    window.localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, JSON.stringify(tab));
  } catch {
    // If localStorage is not available, we can ignore the error
  }
}

export function openDevHelperTab(tab: DevHelperTab) {
  setDevHelperActiveTab(tab);
  $isDevHelperOpen.set(true);
}

export function toggleDevHelper() {
  $isDevHelperOpen.set(!$isDevHelperOpen.get());
}

export function shouldShowDevHelper(hasProductionAccess = false): boolean {
  const isDeployedDevEnv = __CIO_ENV__ === 'standalone' || __CIO_ENV__ === 'devbox';
  const isManuallyHidden = window.sessionStorage.getItem('hideDevUI') === 'true';
  return (
    (isLocalDevHelperEnvironment() ||
      isDeployedDevEnv ||
      (isProductionDevHelperEnvironment() && hasProductionAccess)) &&
    !isManuallyHidden
  );
}
